import type { BoxProps } from '@chakra-ui/react';
import { HStack, Text } from '@chakra-ui/react';
import type { VoteCounts, VoteDirection } from 'hooks/reviews/useReviewVote';
import { useReviewVote } from 'hooks/reviews/useReviewVote';
import { ReviewVote, useLocalStorage } from 'hooks/userPreferences';
import { useState } from 'react';

import { VoteButton } from './VoteButton';

const emptyReviewVotes = { up: false, down: false };

interface ReviewUpDownVoteProps extends BoxProps {
  reviewId: number;
  upVoteCount: number | null;
  downVoteCount: number | null;
  isDownVoteVisible?: boolean;
}

export interface UserReviewVotes {
  up?: boolean;
  down?: boolean;
}

export function ReviewUpDownVote({
  reviewId,
  upVoteCount,
  downVoteCount,
  isDownVoteVisible,
  ...props
}: ReviewUpDownVoteProps) {
  const [hasJustVoted, setHasJustVoted] = useState<VoteDirection | false>(
    false,
  );
  const [voteCounts, setVoteCounts] = useState<VoteCounts>({
    up: upVoteCount,
    down: downVoteCount,
  });
  const { value: storedStates, setStoredValue: setStoredStates } =
    useLocalStorage(ReviewVote);
  const { up, down } = storedStates[reviewId] || emptyReviewVotes;
  const { mutateAsync: updateVote, isPending } = useReviewVote();

  const isDisabled = isPending || !!hasJustVoted || down || up;

  const handleClick = async (voteDirection: VoteDirection) => {
    setHasJustVoted(voteDirection);
    const voteCountsRes = await updateVote({ reviewId, voteDirection });

    const updatedVotesObj = {
      up: voteDirection === 'up' || up,
      down: voteDirection === 'down' || down,
    };

    const updatedUserVotes = {
      ...storedStates,
      [reviewId]: updatedVotesObj,
    };

    setStoredStates(updatedUserVotes);
    setVoteCounts(voteCountsRes);
  };

  return (
    <HStack gap="2xs" {...props}>
      <Text size="detail">Was this helpful?</Text>
      <HStack gap="4xs">
        <VoteButton
          isDisabled={isDisabled}
          voteCount={voteCounts.up}
          hasVoted={up || hasJustVoted === 'up'}
          handleClick={() => handleClick('up')}
        />
        {isDownVoteVisible && (
          <VoteButton
            isDisabled={isDisabled}
            voteCount={voteCounts.down}
            hasVoted={down || hasJustVoted === 'down'}
            isDown
            handleClick={() => handleClick('down')}
          />
        )}
      </HStack>
    </HStack>
  );
}
